import { forwardRef, useMemo } from 'react'
import { Box } from '@mui/material'
import { saveAs, tokens } from '@agribot/polygon-checks-ui'
import { subtotalsByCommodity } from '../rules'
import type { Material, MaterialAnswer, Origin, Taxonomy } from '../types'

/**
 * A material as a tree, left to right, the way the buyer's RFI draws it
 * (Figures 2 and 3): material → commodities (share of the material) →
 * ingredients (share of the material) → origins (share of that ingredient's
 * volume, by country). Generated from the answer, never drawn by hand.
 *
 * Pure SVG so it can be saved as an image (see `downloadTree`); a hidden
 * list carries the same content for screen readers. Labels come in as
 * props, so it needs no translation setup of its own.
 */

export interface TreeLabels {
  title: string
  commodities: string
  ingredients: string
  origins: string
  noOrigins: string
  /** "Chile", "Peru": the country an origin is in. */
  countryName: (code: string) => string
  /** A share as the reader writes one ("33,3 %"); `33.3%` by default. */
  pct?: (value: number) => string
}

const ROW = 26
const HEAD = 34
const PAD = 12
const GAP = 56
const COL = { material: 170, commodity: 150, ingredient: 210, origin: 190 }
const FONT = `12px ${tokens.font.sans}`

const englishPct = (v: number) => `${v.toLocaleString('en-GB', { maximumFractionDigits: 1 })}%`

let ctx: CanvasRenderingContext2D | null | undefined
/** Shortens `text` to fit `width` px, with an ellipsis. Measured with a canvas where there is one. */
function fit(text: string, width: number): string {
  if (ctx === undefined) {
    try { ctx = document.createElement('canvas').getContext('2d') } catch { ctx = null }
    if (ctx) ctx.font = FONT
  }
  const measure = (s: string) => (ctx ? ctx.measureText(s).width : s.length * 6.4)
  if (measure(text) <= width) return text
  let s = text
  while (s.length > 1 && measure(`${s}…`) > width) s = s.slice(0, -1)
  return `${s}…`
}

interface Node { x: number; y: number; w: number; label: string; full: string; tone: keyof typeof TONES }

const TONES = {
  material: { fill: tokens.color.brandDeep, text: tokens.color.ink0 },
  commodity: { fill: tokens.color.brandTeal100, text: '#18576e' },
  ingredient: { fill: tokens.color.brandGreen100, text: '#1f4741' },
  origin: { fill: tokens.color.tintWarning, text: '#6b4c12' },
  failed: { fill: tokens.color.tintDanger, text: '#7a3020' },
  missing: { fill: tokens.color.ink100, text: tokens.color.danger },
} as const

export const CompositionTree = forwardRef<SVGSVGElement, {
  material: Material
  answer: MaterialAnswer | undefined
  taxonomy: Taxonomy
  origins: Origin[]
  labels: TreeLabels
}>(function CompositionTree({ material, answer, taxonomy, origins, labels }, ref) {
  const pct = labels.pct ?? englishPct
  const layout = useMemo(() => {
    const lines = answer?.composition ?? []
    const ingredientOf = new Map(taxonomy.ingredients.map((f) => [f.id, f]))
    const originOf = new Map(origins.map((o) => [o.id, o]))
    const subtotals = subtotalsByCommodity(lines, taxonomy)
    const xs = {
      material: PAD,
      commodity: PAD + COL.material + GAP,
      ingredient: PAD + COL.material + COL.commodity + GAP * 2,
      origin: PAD + COL.material + COL.commodity + COL.ingredient + GAP * 3,
    }
    const nodes: Node[] = []
    const edges: { from: Node; to: Node }[] = []
    const rowY = (i: number) => HEAD + i * ROW + ROW / 2
    let leaf = 0

    const commodityNodes: Node[] = []
    for (const commodityId of subtotals.keys()) {
      const commodity = taxonomy.commodities.find((c) => c.id === commodityId)
      const first = leaf
      const ingredientNodes: Node[] = []
      for (const line of lines.filter((l) => ingredientOf.get(l.ingredientId)?.commodityId === commodityId)) {
        const ingredient = ingredientOf.get(line.ingredientId)
        const allocations = answer?.allocations[line.ingredientId] ?? []
        const startLeaf = leaf
        const originNodes: Node[] = allocations.length
          ? allocations.map((a) => {
              const o = originOf.get(a.originId)
              const country = o ? labels.countryName(o.countryCode) : ''
              const failed = o?.check.state === 'failed'
              const label = `${pct(a.volumeSharePct)} ${country}`
              return {
                x: xs.origin, y: rowY(leaf++), w: COL.origin,
                label: fit(`${failed ? '⚠ ' : ''}${label}`, COL.origin - 14),
                full: `${label} · ${o?.name ?? ''}${failed && o?.check.state === 'failed' ? ` · ${o.check.reason}` : ''}`,
                tone: failed ? 'failed' : 'origin',
              } satisfies Node
            })
          : [{ x: xs.origin, y: rowY(leaf++), w: COL.origin, label: labels.noOrigins, full: labels.noOrigins, tone: 'missing' }]
        const fy = (rowY(startLeaf) + rowY(leaf - 1)) / 2
        const name = `${pct(line.sharePct)} ${ingredient?.name ?? ''}`
        const fnode: Node = { x: xs.ingredient, y: fy, w: COL.ingredient, label: fit(name, COL.ingredient - 14), full: name, tone: 'ingredient' }
        nodes.push(fnode, ...originNodes)
        for (const o of originNodes) edges.push({ from: fnode, to: o })
        ingredientNodes.push(fnode)
      }
      const cy = (rowY(first) + rowY(leaf - 1)) / 2
      const name = `${pct(subtotals.get(commodityId) ?? 0)} ${commodity?.name ?? ''}`
      const cnode: Node = { x: xs.commodity, y: cy, w: COL.commodity, label: fit(name, COL.commodity - 14), full: name, tone: 'commodity' }
      nodes.push(cnode)
      for (const f of ingredientNodes) edges.push({ from: cnode, to: f })
      commodityNodes.push(cnode)
    }
    const my = leaf ? (rowY(0) + rowY(leaf - 1)) / 2 : rowY(0)
    const mnode: Node = { x: xs.material, y: my, w: COL.material, label: fit(material.name, COL.material - 14), full: `${material.name} (${material.code})`, tone: 'material' }
    nodes.push(mnode)
    for (const c of commodityNodes) edges.push({ from: mnode, to: c })
    return { nodes, edges, xs, width: xs.origin + COL.origin + PAD, height: HEAD + Math.max(1, leaf) * ROW + PAD }
  }, [material, answer, taxonomy, origins, labels])

  const curve = (a: Node, b: Node) => {
    const x1 = a.x + a.w
    const x2 = b.x
    const mid = (x1 + x2) / 2
    return `M${x1},${a.y} C${mid},${a.y} ${mid},${b.y} ${x2},${b.y}`
  }
  const heads: [number, string][] = [
    [layout.xs.commodity, labels.commodities],
    [layout.xs.ingredient, labels.ingredients],
    [layout.xs.origin, labels.origins],
  ]

  return (
    <Box sx={{ overflowX: 'auto', maxWidth: '100%' }}>
      <svg
        ref={ref}
        xmlns="http://www.w3.org/2000/svg"
        width={layout.width}
        height={layout.height}
        viewBox={`0 0 ${layout.width} ${layout.height}`}
        role="img"
        aria-label={labels.title}
        style={{ display: 'block', fontFamily: tokens.font.sans }}
      >
        <rect width={layout.width} height={layout.height} fill={tokens.color.ink0} />
        {heads.map(([x, text]) => (
          <text key={text} x={x + 4} y={18} fontSize={10.5} fontWeight={600} letterSpacing="0.08em" fill={tokens.color.ink400}>{text.toUpperCase()}</text>
        ))}
        {layout.edges.map((e, i) => (
          <path key={i} d={curve(e.from, e.to)} fill="none" stroke={tokens.color.ink300} strokeWidth={1.2} />
        ))}
        {layout.nodes.map((n, i) => (
          <g key={i}>
            <title>{n.full}</title>
            <rect x={n.x} y={n.y - 10} width={n.w} height={20} rx={4} fill={TONES[n.tone].fill} />
            <text x={n.x + 7} y={n.y + 4} fontSize={12} fontWeight={n.tone === 'material' ? 600 : 500} fill={TONES[n.tone].text}>{n.label}</text>
          </g>
        ))}
      </svg>
      <Box component="ul" sx={{ position: 'absolute', width: 1, height: 1, overflow: 'hidden', clip: 'rect(0 0 0 0)' }}>
        {layout.nodes.slice().reverse().map((n, i) => <li key={i}>{n.full}</li>)}
      </Box>
    </Box>
  )
})

/** Saves a tree (or any of these SVG diagrams) as an SVG or PNG file. */
export async function downloadTree(svg: SVGSVGElement, name: string, format: 'svg' | 'png') {
  const xml = new XMLSerializer().serializeToString(svg)
  const svgBlob = new Blob([xml], { type: 'image/svg+xml;charset=utf-8' })
  const save = (blob: Blob, ext: string) => saveAs(blob, `${name}.${ext}`)
  if (format === 'svg') return save(svgBlob, 'svg')
  const scale = 2
  const img = new Image()
  const url = URL.createObjectURL(svgBlob)
  await new Promise<void>((resolve, reject) => {
    img.onload = () => resolve()
    img.onerror = () => reject(new Error('The diagram could not be drawn as an image.'))
    img.src = url
  })
  const canvas = document.createElement('canvas')
  canvas.width = svg.width.baseVal.value * scale
  canvas.height = svg.height.baseVal.value * scale
  const c = canvas.getContext('2d')!
  c.scale(scale, scale)
  c.drawImage(img, 0, 0)
  URL.revokeObjectURL(url)
  await new Promise<void>((resolve) => canvas.toBlob((b) => { if (b) save(b, 'png'); resolve() }, 'image/png'))
}
